import axios, { AxiosResponse } from "axios";
import posthog from "posthog-js";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { useSelector, AppDispatch } from "./types";
import { Currency, MapStyle } from "../types";
import { BACKEND_URL } from "../constants";
import { Database } from "./db";
import FileSaver from "file-saver";
import { PriceRequest, PriceResponse } from "../types/api";

// Redux Toolkit migration complete - all thunks now use RTK patterns

const IMAGE_URL_KEY = "xx-mappin-map-77";
const IMAGE_TOKEN_KEY = "xx-mappin-token-33";

const db = new Database();

export type ImageState = {
  generatedImage?: string;
  imagePreview?: string;
  checkoutURL?: string;
  loading?: boolean;
  error?: string;
  currency: Currency;
  price: number;
  priceFormatted: string;
  priceIsLoading: boolean;
};

export type GenerateImageArgs = {
  pins: { lat: number, lng: number }[],
  minLat: number,
  minLng: number,
  maxLat: number,
  size: string,
  orientation: string,
  style: MapStyle,
};

const initialState: ImageState = {
  currency: getCurrencyGuess(),
  price: 0,
  priceFormatted: "",
  priceIsLoading: false,
};

function getCurrencyGuess(): Currency {
  const offset = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(offset / 60);
  if (offsetHours < -7) {
    return Currency.AUD;
  } else if (offsetHours < -5.5 ) {
    return Currency.BDT;
  } else if (offsetHours < -4) {
    return Currency.INR;
  } else if (offsetHours < -0.5) {
    return Currency.EUR;
  } else if (offsetHours > 0.5) {
    return Currency.GBP;
  } else if (offsetHours < 2) {
    return Currency.BRL;
  } else if (offsetHours < 6) {
    return Currency.MXN;
  }
  return Currency.USD;
}

// Async thunk for downloading generated image
export const downloadGeneratedImage = createAsyncThunk(
  'image/downloadGeneratedImage',
  async (_, { dispatch }) => {
    const generatedImage = await db.images.get(IMAGE_URL_KEY);
    if (generatedImage?.img) {
      const img = await (await fetch(generatedImage.img)).blob();
      FileSaver.saveAs(img, "mappin_img.png");
      return generatedImage.img;
    }
    throw new Error("No generated image found");
  }
);

// Async thunk for loading image from cache
export const loadImageFromCache = createAsyncThunk(
  'image/loadImageFromCache',
  async () => {
    const generatedImage = await db.images.get(IMAGE_URL_KEY);
    if (generatedImage?.img) {
      return generatedImage.img;
    }
    return null;
  }
);

// Create the image slice
const imageSlice = createSlice({
  name: 'image',
  initialState,
  reducers: {
    generateImageLoading: (state) => {
      state.error = undefined;
      state.loading = true;
    },
    setCurrency: (state, action: PayloadAction<Currency>) => {
      state.currency = action.payload;
    },
    setPrice: (state, action: PayloadAction<{ price: number; formatted: string }>) => {
      state.price = action.payload.price;
      state.priceFormatted = action.payload.formatted;
      state.priceIsLoading = false;
    },
    setPriceIsLoading: (state, action: PayloadAction<boolean>) => {
      state.priceIsLoading = action.payload;
    },
    generateImageSuccess: (state, action: PayloadAction<string>) => {
      state.error = undefined;
      state.loading = false;
      state.generatedImage = action.payload;
    },
    generateImagePreviewSuccess: (state, action: PayloadAction<{ image: string; checkoutURL: string | null }>) => {
      state.error = undefined;
      state.loading = false;
      state.imagePreview = action.payload.image;
      state.checkoutURL = action.payload.checkoutURL ?? undefined;
    },
    generateImageError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload || "";
      state.loading = false;
      state.generatedImage = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle downloadGeneratedImage
      .addCase(downloadGeneratedImage.fulfilled, (state, action) => {
        state.error = undefined;
        state.loading = false;
        state.generatedImage = action.payload;
      })
      .addCase(downloadGeneratedImage.rejected, (state, action) => {
        state.error = action.error.message || "Failed to download image";
        state.loading = false;
      })
      // Handle loadImageFromCache
      .addCase(loadImageFromCache.fulfilled, (state, action) => {
        if (action.payload) {
          state.error = undefined;
          state.loading = false;
          state.generatedImage = action.payload;
        }
      })
      .addCase(getMapPrice.fulfilled, (state, action) => {
        if (action.payload) {
          state.price = action.payload.price;
          state.priceFormatted = action.payload.formatted;
        }
        state.priceIsLoading = false;
      })
      .addCase(loadImageFromCache.rejected, (state) => {
        // Silently fail for cache loading
      });
  },
});

// Export actions
export const {
  generateImageLoading,
  setCurrency,
  setPrice,
  setPriceIsLoading,
  generateImageSuccess,
  generateImagePreviewSuccess,
  generateImageError,
} = imageSlice.actions;

// Export reducer (for backward compatibility, keep the old name)
export const imageStore = imageSlice.reducer;

export const fetchMap = async (
  token: string,
  processUrl: (url: string) => void,
  stopRetry: (error?: string) => void,
): Promise<void> => {
  try {
    const res = await axios.get<{ url: string, status: string }>(`${BACKEND_URL}/map/${token}`);
    if (res.status >= 200 && res.status <= 300 && res.data?.url) {
      processUrl(res.data.url);
    } else if (res.status >= 200 && res.status <= 300 && res.data?.status === "failure") {
      stopRetry();
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      stopRetry(e.response?.data?.msg);
    }
    return undefined;
  }
};

export const getMapPreview = async (
  token: string,
  processUrl: (url: string) => Promise<void>,
  stopRetry: (error?: string) => void,
): Promise<void> => {
  try {
    const res = await axios.get<{ url: string, status: string }>(`${BACKEND_URL}/map/preview/${token}`);
    if (res.status >= 200 && res.status <= 300 && res.data?.url) {
      await processUrl(res.data.url);
    } else if (res.status >= 200 && res.status <= 300 && res.data?.status === "failure") {
      stopRetry();
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      stopRetry(e.response?.data?.msg);
    }
    return undefined;
  }
};

export const fetchImage = (maybeToken?: string) => async (dispatch: AppDispatch) => {
  const token = maybeToken || (await db.tokens.get(IMAGE_TOKEN_KEY))?.token;
  if (!token) {
    return dispatch(generateImageError("Token not found. If you purchased a map, please contact support."));
  }

  dispatch(generateImageLoading());
  const url = await new Promise<string | undefined>((resolve, reject) => {
    let poller: NodeJS.Timeout;
    const succeed = (url: string) => {
      clearInterval(poller);
      posthog.capture("image_generated_success", { token });
      dispatch(generateImageSuccess(url));
      resolve(url);
    };
    const fail = (e: string = "Unknown error when creating image") => {
      clearInterval(poller);
      dispatch(generateImageError(e));
      posthog.capture("image_generated_error", { token });
      resolve(undefined);
    };
    let i = 0;
    poller = setInterval(
      () => {
        fetchMap(token, succeed, fail);
        i += 1;
        if (i === 40) {
          clearInterval(poller);
          resolve(undefined);
        }
      },
      5 * 1000,
    );
  });
  if (!url) {
    return dispatch(generateImageError("Something went wrong getting the image URL. Please contact support."));
  }
  db.images.put({ id: IMAGE_URL_KEY, img: url });
}


export const getMap = (token: string) => async (dispatch: AppDispatch) => {
  const res = await axios.get<{ url: string }>(`${BACKEND_URL}/v2/map/${token}`);
  const url = res.data.url;
  if (url) {
    posthog.capture("image_generated_success", { token });
    dispatch(generateImageSuccess(url));
    db.images.put({ id: IMAGE_URL_KEY, img: url });
  }
}

export const getImagePreview = (token: string) => async (dispatch: AppDispatch) => {
  dispatch(generateImageLoading());
  const url = await new Promise<string | undefined>((resolve, reject) => {
    let poller: NodeJS.Timeout;
    const succeed = async (url: string) => {
      clearInterval(poller);
      const checkoutRes = await axios.get<{ sessionURL: string | null }>(`${BACKEND_URL}/map/checkout/${token}`);
      posthog.capture("image_generated_success", { token });
      dispatch(generateImagePreviewSuccess({ image: url, checkoutURL: checkoutRes.data.sessionURL }));
      resolve(url);
    };
    const fail = (e: string = "Unknown error when creating image") => {
      clearInterval(poller);
      dispatch(generateImageError(e));
      posthog.capture("image_generated_error", { token });
      resolve(undefined);
    };
    let i = 0;
    poller = setInterval(
      () => {
        getMapPreview(token, succeed, fail);
        i += 1;
        if (i === 40) {
          clearInterval(poller);
          resolve(undefined);
        }
      },
      5 * 1000,
    );
  });
  if (!url) {
    return dispatch(generateImageError("Something went wrong getting the image URL. Please contact support."));
  }
  db.images.put({ id: IMAGE_URL_KEY, img: url });
}

export const getImageCheckout = async (token: string): Promise<string | null> => {
  const res = await axios.get<{ sessionUrl: string }>(
    `${BACKEND_URL}/checkout/${token}`,
  );
  if (res.status === 200 && res.data.sessionUrl) {
    return res.data.sessionUrl;
  }
  return null;
}

export const createMap = (args: GenerateImageArgs) =>
  async (dispatch: AppDispatch) => {
    dispatch(generateImageLoading());
    try {
      const res = await axios.post<{ token: string }>(
        `${BACKEND_URL}/map`,
        { ...args },
      );

      const { token } = res.data;
      posthog.capture("map_job_start_success", args);

      if (res.status >= 200 && res.status < 300 && token) {
        await db.tokens.put({ id: IMAGE_TOKEN_KEY, token });
        if ((await db.tokens.get(IMAGE_TOKEN_KEY))?.token === token) {
          await db.images.delete(IMAGE_URL_KEY);
          window.location.assign(`/preview/${token}`);
          return
        }
      }
    } catch (e) {
      posthog.capture("map_job_start_fail", { ...args, status: (e as any).status, data: (e as any).data?.toString()});
      const res = axios.isAxiosError(e) ? e.response as AxiosResponse<{ error: true, msg?: string }> : null;
      console.error("Received error", e);
      dispatch(generateImageError(res?.data?.msg || "Unknown error when creating image"));
      return;
    }
  };

export const getMapPrice = createAsyncThunk(
  'image/price',
  async (request: PriceRequest, { dispatch }) => {
    dispatch(setPriceIsLoading(true));
    try {
      const res = await axios.post<PriceResponse>(
        `${BACKEND_URL}/price`,
        request,
      );

      const { price, formatted } = res.data;
      dispatch(setPrice({ price, formatted }));
      return { price, formatted };
    } catch (e) {
      console.error("Received error fetching price", e);
      dispatch(setPriceIsLoading(false));
      return null;
    }
  }
);

export const triggerGenerateImage = (args: GenerateImageArgs) =>
  async (dispatch: AppDispatch) => {
    dispatch(generateImageLoading());
    try {
      const res = await axios.post<{ token: string, total: number, sessionUrl: string }>(
        `${BACKEND_URL}/pricing`,
        { ...args },
      );

      const { token, sessionUrl } = res.data;

      posthog.capture(
        "map_checkout_success",
        { ...args, paid: sessionUrl.indexOf("mappin.pro") !== -1 }
      );

      if (res.status >= 200 && res.status < 300 && token) {
        await db.tokens.put({ id: IMAGE_TOKEN_KEY, token });
        if ((await db.tokens.get(IMAGE_TOKEN_KEY))?.token === token) {
          await db.images.delete(IMAGE_URL_KEY);
          window.location.assign(sessionUrl);
          return
        }
      }
    } catch (e) {
      posthog.capture("map_checkout_fail", { ...args, status: (e as any).status, data: (e as any).data?.toString()});
      const res = axios.isAxiosError(e) ? e.response as AxiosResponse<{ error: true, msg?: string }> : null;
      console.error("Received error", e);
      dispatch(generateImageError(res?.data?.msg || "Unknown error when creating image"));
      return;
    }
  };

// These functions are now replaced by createAsyncThunk versions above

export const useImageAPIError = (): string | undefined =>
  useSelector((state) => state.image.error);

export const useIsLoading = (): boolean =>
  useSelector((state) => !!state.image.loading);

export const useGeneratedImage = (): string | undefined =>
  useSelector((state) => state.image.generatedImage);

export const useImagePreview = (): string | undefined =>
  useSelector((state) => state.image.imagePreview);

export const useCheckout = (): string | undefined =>
  useSelector((state) => state.image.checkoutURL);

export const usePrice = (): number =>
  useSelector((state) => state.image.price);

export const usePriceFormatted = (): string =>
  useSelector((state) => state.image.priceFormatted);

export const usePriceIsLoading = (): boolean =>
  useSelector((state) => state.image.priceIsLoading);

export const useCurrency = (): Currency =>
  useSelector((state) => state.image.currency);
